import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  BoltOutlined as BoltIcon,
  InsightsOutlined as InsightsIcon,
} from '@mui/icons-material';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAuth } from '../../context/AuthContext';

const microsoftClientId = process.env.REACT_APP_MICROSOFT_LOGIN_CLIENT_ID;
const msalInstance = microsoftClientId
  ? new PublicClientApplication({
      auth: {
        clientId: microsoftClientId,
        authority: 'https://login.microsoftonline.com/organizations',
        redirectUri: typeof window !== 'undefined' ? window.location.origin + '/login' : undefined,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    })
  : null;

let msalInitPromise = null;
const ensureMsalInitialized = () => {
  if (!msalInstance) return Promise.resolve();
  if (!msalInitPromise) msalInitPromise = msalInstance.initialize();
  return msalInitPromise;
};

const Login = () => {
  const navigate = useNavigate();
  const { login, googleLogin, microsoftLogin, verifyMfa, isAuthenticated, error: authError, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // 2-step verification: set when a sign-in needs the authenticator code.
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (authError && submitted) setError(authError);
  }, [authError, submitted]);

  const handleGoogleCredential = async (response) => {
    if (!response?.credential) return;
    setSubmitted(true);
    try {
      const result = await googleLogin(response.credential);
      if (result.mfaRequired) {
        setError('');
        setMfaToken(result.mfaToken);
      } else if (result.success) {
        navigate('/');
      } else if (result.errorCode === 'NO_ACCOUNT') {
        setError('No account found for this Google email. Ask your admin to invite you, or contact support@superchargeme.com.');
      } else {
        setError(result.error || 'Google sign-in failed');
      }
    } catch (err) {
      setError('An unexpected error occurred during Google sign-in');
      console.error(err);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!msalInstance) {
      setError('Microsoft sign-in is not configured');
      return;
    }
    setSubmitted(true);
    try {
      await ensureMsalInitialized();
      const result = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account',
      });
      if (!result || !result.idToken) {
        setError('Microsoft sign-in did not return an ID token');
        return;
      }
      const r = await microsoftLogin(result.idToken);
      if (r.mfaRequired) {
        setError('');
        setMfaToken(r.mfaToken);
      } else if (r.success) {
        navigate('/');
      } else if (r.errorCode === 'NO_ACCOUNT') {
        setError('No account found for this Microsoft email. Ask your admin to invite you, or contact support@superchargeme.com.');
      } else if (r.errorCode === 'PERSONAL_MS_ACCOUNT') {
        setError('This email is a personal Microsoft account, not a work account. Please sign in with Google or email/password instead.');
      } else {
        setError(r.error || 'Microsoft sign-in failed');
      }
    } catch (err) {
      if (err && (err.errorCode === 'user_cancelled' || err.errorCode === 'popup_window_error')) return;
      setError('An unexpected error occurred during Microsoft sign-in');
      console.error(err);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID;
    if (!clientId) return;

    // Inject Google Identity Services script if not already present.
    // (index.html is gitignored in this repo, so we can't add the tag there.)
    if (!document.getElementById('gis-client-script')) {
      const s = document.createElement('script');
      s.id = 'gis-client-script';
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      const g = window.google?.accounts?.id;
      if (!g) {
        setTimeout(tryRender, 100);
        return;
      }
      g.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
      });
      const container = document.getElementById('google-signin-button');
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
        g.renderButton(container, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          width: container.clientWidth || 320,
        });
      }
    };
    tryRender();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError('Email is required');
    if (!password.trim()) return setError('Password is required');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return setError('Please enter a valid email address');
    setSubmitted(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setError('');
        setMfaToken(result.mfaToken);
      } else if (result.success) navigate('/');
      else setError(result.error || 'Login failed');
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (!mfaCode.trim()) return setError('Enter the code from your authenticator app');
    const result = await verifyMfa(mfaToken, mfaCode.trim());
    if (result.success) {
      navigate('/');
    } else if (result.code === 'MFA_TOKEN_INVALID') {
      setMfaToken(null);
      setMfaCode('');
      setError(result.error || 'Your sign-in has expired. Please sign in again.');
    } else {
      setError(result.error || 'That code is not valid');
    }
  };

  const features = [
    { icon: <InsightsIcon />, title: 'Unified analytics', desc: 'Every metric from every platform, one pane of glass.' },
    { icon: <BoltIcon />, title: 'AI agents that act', desc: 'Specialists that answer your questions and execute the work — from launching ads to publishing reports.' },
    { icon: <TrendingUpIcon />, title: 'Always-on operations', desc: 'Live data, recurring reports, scheduled actions — your business runs on autopilot.' },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* Left marketing panel — desktop only */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--gradient-marketing)',
          color: '#fff',
        }}
      >
        {/* Decorative radials */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
          background: `
            radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18) 0%, transparent 40%),
            radial-gradient(circle at 85% 75%, rgba(20,184,166,0.3) 0%, transparent 45%),
            radial-gradient(circle at 50% 110%, rgba(139,92,246,0.4) 0%, transparent 50%)
          `,
        }} />

        {/* Top — logo */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.25)',
          }}>
            <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 800,
            fontSize: '1.05rem',
            letterSpacing: '-0.02em',
          }}>
            SUPERCHARGEME
          </Typography>
        </Box>

        {/* Middle — headline + features */}
        <Box sx={{ position: 'relative', maxWidth: 480 }}>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontSize: { md: '2.5rem', lg: '3rem' },
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            mb: 2,
          }}>
            Your data,<br/>supercharged.
          </Typography>
          <Typography sx={{
            fontSize: '1.05rem',
            opacity: 0.85,
            mb: 5,
            lineHeight: 1.55,
            fontWeight: 400,
          }}>
            The unified data platform that connects every source, answers every question,
            and runs every workflow.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {features.map((f) => (
              <Box key={f.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5,
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  '& svg': { color: '#fff', fontSize: 20 },
                }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.25 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', opacity: 0.8, lineHeight: 1.5 }}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom — footer */}
        <Typography sx={{ position: 'relative', fontSize: '0.8rem', opacity: 0.65 }}>
          © {new Date().getFullYear()} SuperChargeMe. All rights reserved.
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
          py: 6,
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile logo */}
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center', gap: 1.25, mb: 4,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'var(--gradient-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 800,
              fontSize: '1rem',
              letterSpacing: '-0.02em',
            }}>
              SUPERCHARGEME
            </Typography>
          </Box>

          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            {mfaToken ? '2-step verification' : 'Welcome back'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            {mfaToken
              ? 'Enter the 6-digit code from your authenticator app. Lost your phone? Enter one of your recovery codes instead.'
              : 'Sign in to your account to continue.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {mfaToken && (
            <Box component="form" onSubmit={handleVerifyMfa} noValidate>
              <TextField
                required fullWidth autoFocus
                id="mfa-code" label="Code" name="mfa-code"
                autoComplete="one-time-code"
                inputProps={{ inputMode: 'text', maxLength: 11 }}
                value={mfaCode}
                onChange={(e) => { setMfaCode(e.target.value); setError(''); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, borderRadius: 2, position: 'relative' }}
              >
                {loading ? (
                  <CircularProgress
                    size={22}
                    sx={{ color: 'white', position: 'absolute', top: '50%', left: '50%', marginTop: '-11px', marginLeft: '-11px' }}
                  />
                ) : 'Verify'}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); setSubmitted(false); }}
                  sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}

          {/* Kept mounted (hidden) during the code step so the Google button rendered into it survives "Back to sign in". */}
          <Box component="form" onSubmit={handleLogin} noValidate autoComplete="on" sx={{ display: mfaToken ? 'none' : 'block' }}>
            <TextField
              required fullWidth autoFocus
              id="email" label="Email" name="email" type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              required fullWidth
              name="password" label="Password" id="current-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: 2,
                position: 'relative',
              }}
            >
              {loading ? (
                <CircularProgress
                  size={22}
                  sx={{
                    color: 'white',
                    position: 'absolute',
                    top: '50%', left: '50%',
                    marginTop: '-11px', marginLeft: '-11px',
                  }}
                />
              ) : 'Sign In'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', letterSpacing: 1 }}>
                OR
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>

            <Box id="google-signin-button" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }} />

            {microsoftClientId && (
              <Button
                fullWidth
                onClick={handleMicrosoftLogin}
                variant="outlined"
                disabled={submitted}
                sx={{
                  mt: 1.5,
                  py: 1.25,
                  borderColor: 'divider',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
                startIcon={
                  <Box component="svg" viewBox="0 0 23 23" sx={{ width: 18, height: 18 }}>
                    <path fill="#f25022" d="M1 1h10v10H1z" />
                    <path fill="#00a4ef" d="M1 12h10v10H1z" />
                    <path fill="#7fba00" d="M12 1h10v10H12z" />
                    <path fill="#ffb900" d="M12 12h10v10H12z" />
                  </Box>
                }
              >
                Continue with Microsoft
              </Button>
            )}

            <Typography sx={{
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'text.secondary',
              mt: 4,
            }}>
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to="/register"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Create one
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
